import { useState } from 'react';
import {
  ChevronDown, Search, Keyboard, BarChart2, Repeat, TrendingUp, CreditCard,
  ArrowLeftRight, LayoutDashboard, Wallet, Tags, Target, Users, Sparkles,
  FlaskConical, Star, Gift,
} from 'lucide-react';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import { clsx } from 'clsx';

const faqData = [
  {
    category: 'Dashboard',
    icon: LayoutDashboard,
    color: 'blue',
    items: [
      { question: 'O que e o Dashboard?', answer: 'O Dashboard e sua visao geral financeira. Ele exibe o patrimonio total (soma de todas as contas), receitas e despesas do mes atual, taxa de poupanca, evolucao patrimonial dos ultimos 6 meses, top categorias de gastos, projecao de fluxo de caixa e proximos pagamentos. O ticker de mercado no topo mostra cotacoes em tempo real.' },
      { question: 'Como funciona a projecao de fluxo de caixa?', answer: 'A projecao analisa suas recorrencias (contas fixas) e parcelamentos ativos para estimar receitas e despesas dos proximos 6 meses. O grafico mostra barras de receita (verde) e despesa (vermelho), com uma linha de saldo projetado (azul). Quanto mais recorrencias voce cadastrar, mais precisa sera a projecao.' },
      { question: 'O que e a taxa de poupanca?', answer: 'E a porcentagem da sua renda que sobra apos os gastos. Acima de 20% e considerado excelente, entre 10-20% e bom, entre 0-10% e regular, e negativo significa que voce esta gastando mais do que ganha. O anel colorido muda de cor conforme sua taxa.' },
    ],
  },
  {
    category: 'Contas',
    icon: Wallet,
    color: 'purple',
    items: [
      { question: 'Como gerenciar minhas contas?', answer: 'Na pagina de Contas, voce pode cadastrar contas correntes, poupancas, carteiras digitais e cartoes. Cada conta tem nome, tipo, saldo inicial, icone e cor personalizaveis. O patrimonio total e a soma dos saldos de todas as contas.' },
      { question: 'Posso ter contas com saldo negativo?', answer: 'Sim. Contas com saldo negativo (como cheque especial ou cartao de credito) sao exibidas com destaque em vermelho. Elas sao contabilizadas normalmente no patrimonio total.' },
      { question: 'O que sao contas pessoais vs compartilhadas?', answer: 'No plano Familia, voce pode marcar contas como "pessoais" (visiveis apenas para voce) ou "compartilhadas" (visiveis para todos os membros da familia). Contas compartilhadas aparecem no dashboard familiar.' },
    ],
  },
  {
    category: 'Transacoes',
    icon: ArrowLeftRight,
    color: 'blue',
    items: [
      { question: 'Como registrar transacoes?', answer: 'Clique em "Nova Transacao" e preencha: tipo (receita ou despesa), descricao, valor, data, conta e categoria. Voce pode tambem importar transacoes em lote via arquivo OFX do seu banco.' },
      { question: 'Qual a diferenca entre realizada e prevista?', answer: 'Transacoes "realizadas" ja aconteceram e afetam o saldo atual. "Previstas" sao futuras (vindas de recorrencias ou parcelamentos) e aparecem na projecao. Ao confirmar uma prevista, ela se torna realizada.' },
      { question: 'Como importar um extrato OFX?', answer: 'Clique em "Importar OFX", arraste o arquivo ou selecione-o. O sistema identifica automaticamente receitas e despesas. Voce precisa escolher a conta de destino e atribuir categorias a cada transacao (ou usar categoria padrao em lote) antes de importar.' },
      { question: 'Quais sao as visualizacoes disponiveis?', answer: 'Tres modos: Tabela (lista ordenavel com colunas), Cards (agrupados por data com resumo diario) e Timeline (linha do tempo vertical com saldo acumulado por dia). Use os botoes de alternancia acima da lista.' },
      { question: 'Como funciona o calendario de saldo?', answer: 'O botao "Calendario" abre um modal que mostra o saldo projetado para cada dia do mes. Clique em um dia para filtrar as transacoes daquela data. Cores indicam saldo positivo (verde), negativo (vermelho) ou neutro.' },
    ],
  },
  {
    category: 'Categorias',
    icon: Tags,
    color: 'orange',
    items: [
      { question: 'Para que servem as categorias?', answer: 'Categorias classificam suas transacoes em grupos como Alimentacao, Transporte, Salario, etc. Isso permite analisar para onde vai seu dinheiro nos relatorios. Existem categorias de receita e de despesa, cada uma com icone e cor proprios.' },
      { question: 'Como criar categorias padrao?', answer: 'Se voce ainda nao tem categorias, clique em "Criar Padroes" para gerar automaticamente as categorias mais comuns (Alimentacao, Transporte, Moradia, Saude, Lazer, Salario, etc.).' },
    ],
  },
  {
    category: 'Recorrencias',
    icon: Repeat,
    color: 'purple',
    items: [
      { question: 'O que sao recorrencias?', answer: 'Recorrencias sao lancamentos que se repetem automaticamente: aluguel, salario, assinaturas, condominio, etc. Voce define o valor, frequencia (diaria, semanal, mensal, anual), dia do mes e periodo. O sistema gera os lancamentos automaticamente na timeline.' },
      { question: 'Como pausar e retomar?', answer: 'Na lista de recorrencias, use o botao de pausa para suspender temporariamente. A recorrencia para de gerar lancamentos mas mantem todo o historico. Clique em retomar quando quiser reativa-la.' },
      { question: 'Preciso confirmar cada lancamento?', answer: 'Sim. Lancamentos gerados por recorrencias aparecem como "previstos" na timeline. Voce confirma cada um clicando em "Lancar", o que cria a transacao real e atualiza o saldo da conta.' },
    ],
  },
  {
    category: 'Parcelamentos',
    icon: CreditCard,
    color: 'orange',
    items: [
      { question: 'Como registrar uma compra parcelada?', answer: 'Clique em "Novo Parcelamento" e informe: descricao, valor total (ou valor da parcela), numero de parcelas, data de inicio, conta e categoria. O sistema cria automaticamente todas as parcelas mensais.' },
      { question: 'Como pagar uma parcela?', answer: 'Na timeline de transacoes, parcelas pendentes aparecem com o botao "Pagar". Ao clicar, a parcela e marcada como paga e o saldo da conta e atualizado. Voce tambem pode pagar pelo detalhe do parcelamento.' },
      { question: 'Posso informar valor por parcela?', answer: 'Sim! No formulario de novo parcelamento, use o toggle para alternar entre "Valor Total" e "Valor da Parcela". O sistema calcula o outro valor automaticamente.' },
    ],
  },
  {
    category: 'Investimentos',
    icon: TrendingUp,
    color: 'green',
    items: [
      { question: 'Quais investimentos posso registrar?', answer: 'CDB, LCI, LCA, Tesouro Selic, Tesouro IPCA, Tesouro Prefixado, Poupanca, Fundos, Debentures, CRI, CRA e outros. Cada tipo tem campos especificos de taxa (% CDI, taxa anual, taxa mensal).' },
      { question: 'Como funciona a projecao?', answer: 'O sistema usa as taxas cadastradas para projetar o rendimento futuro. Para investimentos atrelados ao CDI, a projecao usa a taxa CDI atual. Os valores sao estimativas e podem variar.' },
      { question: 'Como registrar aportes e dividendos?', answer: 'No detalhe de cada investimento, use os botoes "Aporte" para registrar novos depositos e "Dividendo" para registrar rendimentos recebidos. O historico completo fica disponivel na aba de movimentacoes.' },
    ],
  },
  {
    category: 'Relatorios',
    icon: BarChart2,
    color: 'indigo',
    items: [
      { question: 'Quais relatorios estao disponiveis?', answer: 'Gastos por categoria (pizza/barras), evolucao patrimonial (linha temporal), comparativo mensal (barras lado a lado), fluxo de caixa projetado e resumo geral. Todos sao interativos e permitem filtrar por periodo.' },
      { question: 'Como exportar dados?', answer: 'Em Configuracoes > Dados, exporte tudo em CSV (para Excel/planilhas) ou JSON (backup completo). Na pagina de Transacoes, o botao "Exportar" gera um CSV das transacoes filtradas.' },
      { question: 'Os dados sao em tempo real?', answer: 'Sim. Ao adicionar, editar ou excluir uma transacao, os relatorios sao atualizados automaticamente. Nao e necessario atualizar a pagina.' },
    ],
  },
  {
    category: 'Simulacoes',
    icon: FlaskConical,
    color: 'cyan',
    items: [
      { question: 'Como criar uma simulacao?', answer: 'Clique em "Nova Simulacao", de um nome e defina o periodo. Adicione eventos como aumentos de renda, novos gastos, cortes de despesa, etc. O sistema calcula o impacto financeiro ao longo do tempo.' },
      { question: 'Posso comparar cenarios?', answer: 'Sim! Crie ate 3 simulacoes diferentes e use a funcao "Comparar" para ve-las lado a lado em um grafico. Isso ajuda a decidir entre opcoes como trocar de emprego, mudar de cidade, etc.' },
    ],
  },
  {
    category: 'Metas e Dividas',
    icon: Target,
    color: 'pink',
    items: [
      { question: 'Como definir uma meta?', answer: 'Crie uma meta com nome, valor alvo, prazo e valor inicial. Registre contribuicoes periodicamente para acompanhar o progresso. A barra de progresso e estimativa de conclusao sao calculadas automaticamente.' },
      { question: 'O que sao metas compartilhadas?', answer: 'No plano Familia, metas marcadas como "compartilhadas" permitem que todos os membros vejam e contribuam. O historico mostra quem contribuiu e quanto.' },
      { question: 'Como funciona o gerenciamento de dividas?', answer: 'Cadastre dividas com valor total, saldo restante, taxa de juros e pagamento minimo. O sistema sugere estrategias de quitacao: avalanche (prioriza maior juros) ou bola de neve (prioriza menor saldo). Registre pagamentos para acompanhar o progresso.' },
    ],
  },
  {
    category: 'Familia',
    icon: Users,
    color: 'green',
    items: [
      { question: 'Como funciona o plano Familia?', answer: 'O plano Familia permite ate 5 usuarios (1 proprietario + 4 membros convidados) compartilharem o mesmo espaco financeiro. O proprietario convida membros por e-mail. Cada membro pode ter contas pessoais (privadas) e contas compartilhadas (visiveis a todos).' },
      { question: 'Quais sao os papeis?', answer: 'Proprietario: controle total, pode convidar/remover membros e transferir propriedade. Admin: pode gerenciar contas e transacoes compartilhadas. Membro: pode ver dados compartilhados e gerenciar suas proprias contas.' },
      { question: 'O que e o Dashboard Familiar?', answer: 'Mostra a visao consolidada: saldo total da familia, atividade por membro, metas compartilhadas e principais categorias de gasto. Acessivel pela aba "Dashboard" na pagina Familia.' },
    ],
  },
  {
    category: 'Consultor IA',
    icon: Sparkles,
    color: 'violet',
    items: [
      { question: 'Como funciona o Consultor IA?', answer: 'O consultor analisa seus dados financeiros reais (contas, transacoes, metas, dividas, orcamentos) e gera insights personalizados. Ele identifica padroes de gasto, oportunidades de economia e riscos financeiros.' },
      { question: 'Posso conversar com o consultor?', answer: 'Sim! A aba "Chat" permite fazer perguntas especificas sobre suas financas. O consultor responde com base nos seus dados reais, oferecendo orientacoes praticas e personalizadas.' },
      { question: 'Os insights sao atualizados?', answer: 'Sim. Cada vez que voce acessa a pagina ou clica em "Atualizar", novos insights sao gerados com base nos dados mais recentes. O consultor considera os ultimos 3 meses de transacoes.' },
    ],
  },
  {
    category: 'Niveis e Conquistas',
    icon: Star,
    color: 'yellow',
    items: [
      { question: 'Como funciona o sistema de niveis?', answer: 'Voce ganha XP (pontos de experiencia) ao usar o MyBank: registrar transacoes, completar metas, manter streaks diarios e mais. Conforme acumula XP, voce sobe de nivel — de Iniciante (nivel 1) ate Lenda Financeira (nivel 10).' },
      { question: 'O que sao streaks?', answer: 'Streak e a sequencia de dias consecutivos em que voce registra algo no MyBank. Cada dia ativo ganha +15 XP de bonus. A cada 7 dias seguidos, voce ganha +50 XP extras.' },
      { question: 'Como desbloquear conquistas?', answer: 'Conquistas sao desbloqueadas automaticamente ao atingir marcos: primeira transacao, streak de 7/30/90 dias, completar metas, indicar amigos, atingir niveis 5 e 10, entre outros. Cada conquista da XP bonus.' },
    ],
  },
  {
    category: 'Indicacoes',
    icon: Gift,
    color: 'emerald',
    items: [
      { question: 'Como funciona o programa de indicacoes?', answer: 'Cada usuario recebe um codigo unico de indicacao. Quando alguem se cadastra usando seu codigo e assina um plano pago, voce ganha comissao recorrente: 9% no nivel 1 (indicado direto), 5% no nivel 2 e 1% no nivel 3.' },
      { question: 'Quando posso sacar minhas comissoes?', answer: 'Voce pode solicitar saque via Pix quando seu saldo atingir R$50,00. O processamento leva ate 3 dias uteis.' },
      { question: 'As comissoes sao recorrentes?', answer: 'Sim! Enquanto o indicado mantiver a assinatura ativa, voce recebe a comissao todo mes sobre o valor pago.' },
      { question: 'Como compartilho meu codigo?', answer: 'Acesse a pagina "Indicar Amigos" no menu lateral. La voce encontra seu codigo, link de indicacao e botao de compartilhar via WhatsApp, redes sociais ou copiar o link.' },
    ],
  },
];

const shortcuts = [
  { keys: ['N'], description: 'Nova transação' },
  { keys: ['?'], description: 'Mostrar atalhos' },
  { keys: ['Ctrl', 'K'], description: 'Busca rápida' },
  { keys: ['Esc'], description: 'Fechar modal' },
  { keys: ['Ctrl', 'D'], description: 'Ir ao Dashboard' },
  { keys: ['Ctrl', 'T'], description: 'Ir a Transações' },
];

const colorMap = {
  blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600',
  orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600',
  green: 'bg-green-100 dark:bg-green-900/30 text-green-600',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600',
  pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600',
  violet: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600',
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <button
        onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-white pr-4">{question}</span>
        <ChevronDown
          size={18}
          className={clsx('text-gray-400 flex-shrink-0 transition-transform', isOpen && 'rotate-180')}
        />
      </button>
      {isOpen && (
        <div className="pb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const Help = () => {
  const [search, setSearch] = useState('');

  const filteredFaq = search.trim()
    ? faqData.map(cat => ({
        ...cat,
        items: cat.items.filter(
          item =>
            item.question.toLowerCase().includes(search.toLowerCase()) ||
            item.answer.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(cat => cat.items.length > 0)
    : faqData;

  return (
    <div>
      <PageHeader
        title="Central de Ajuda"
        subtitle="Encontre respostas e aprenda a usar o MyBank"
      />

      {/* Barra de busca */}
      <div className="relative mb-8 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar nas perguntas frequentes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
        />
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* FAQ — ocupa 3 colunas */}
        <div className="lg:col-span-3 space-y-6">
          {filteredFaq.length === 0 ? (
            <Card>
              <p className="text-center text-gray-500 py-8">Nenhuma pergunta encontrada para "{search}"</p>
            </Card>
          ) : (
            filteredFaq.map(({ category, icon: Icon, color, items }) => (
              <Card key={category} padding="none">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', colorMap[color])}>
                    <Icon size={16} />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{category}</h3>
                </div>
                <div className="px-6">
                  {items.map(item => (
                    <FAQItem key={item.question} {...item} />
                  ))}
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Atalhos de teclado */}
          <Card title="Atalhos de Teclado">
            <div className="space-y-2">
              {shortcuts.map(({ keys, description }) => (
                <div key={description} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{description}</span>
                  <div className="flex items-center gap-1">
                    {keys.map(key => (
                      <kbd
                        key={key}
                        className="px-2 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600"
                      >
                        {key}
                      </kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Versão */}
          <div className="text-center text-xs text-gray-400 space-y-1">
            <p>MyBank v1.0.0</p>
            <p>Feito com ❤ no Brasil</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
